import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { NotificationBell } from './NotificationBell';
import { NotificationDrawer } from './NotificationDrawer';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu,
  Settings,
  Calendar,
  LogOut,
  Star,
  User as UserIcon,
  Search as SearchIcon,
  CreditCard,
  Clock,
  Wallet,
  DollarSign,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const { user } = useAuth();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { theme } = useColorTheme();

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30 h-16">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-full">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger - only show on mobile when user is logged in */}
            {user && (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="md:hidden text-gray-700 hover:text-purple-600 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}

            <Link to={user ? "/home" : "/"} className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Spot Light Logo" 
                className="h-28 w-auto"
              />
            </Link>
          </div>

          <div className="flex items-center">
            {user && (
              <NotificationBell
                onClick={() => setIsNotificationDrawerOpen(true)}
              />
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar - Always visible on desktop */}
        {user && (
          <aside className="hidden md:flex flex-col w-64 border-r border-gray-200 bg-white sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
            <DesktopSidebar />
          </aside>
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col">
          <div className="flex-1">
            {children}
          </div>

          {/* Footer */}
          <footer className="w-full py-6 border-t mt-auto">
            <div className="container mx-auto px-4">
              <div className="flex flex-col items-center space-y-3 text-center text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <Link to="/terms" className={cn("hover:underline transition-colors", theme.secondary)}>
                    Terms of Service
                  </Link>
                  <span>•</span>
                  <Link to="/privacy" className={cn("hover:underline transition-colors", theme.secondary)}>
                    Privacy Policy
                  </Link>
                  <span>•</span>
                  <Link to="/pricing" className={cn("hover:underline transition-colors", theme.secondary)}>
                    Pricing
                  </Link>
                  <span>•</span>
                  <Link to="/refund-policy" className={cn("hover:underline transition-colors", theme.secondary)}>
                    Refund Policy
                  </Link>
                  <span>•</span>
                  <a href="mailto:info@nexbii.com" className={cn("hover:underline transition-colors", theme.secondary)}>
                    Contact
                  </a>
                </div>
                <div>
                  © {new Date().getFullYear()} Nexbii. All rights reserved.
                </div>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* Notification Drawer */}
      {user && (
        <NotificationDrawer
          isOpen={isNotificationDrawerOpen}
          onClose={() => setIsNotificationDrawerOpen(false)}
        />
      )}

      {/* Mobile Sidebar Overlay */}
      {user && (
        <Sidebar
          isOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Desktop Sidebar Component
const DesktopSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {user, logout} = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    {label: 'Find Influencers', icon: SearchIcon, path: '/home'},
    {label: 'My Bookings', icon: Calendar, path: '/my-bookings'},
    {label: 'Buy Credits', icon: CreditCard, path: '/buy-credits'},
    ...(user?.isStarAccount ? [{label: 'Payouts & Earnings', icon: DollarSign, path: '/payouts'}] : []),
    {label: 'Settings', icon: Settings, path: '/settings'},
  ];

  return (
    <>
      {/* User Info Header */}
      <div className="p-4 border-b border-gray-200">
        {user && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-5 h-5 text-gray-500" />
              </div>
              <span className="text-sm font-medium text-gray-700 truncate">
                {user.name || user.displayName || user.email}
              </span>
            </div>
            {user.credits !== undefined && (
              <div 
                className="flex items-center gap-1.5 text-sm bg-gray-100 px-2.5 py-1.5 rounded-full self-start" 
                title={`${user.credits} Credits`}
              >
                <Wallet className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="font-medium text-gray-700">{user.credits?.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {menuItems.map(item => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all',
                isActive
                  ? 'bg-purple-100 text-purple-700 font-semibold'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600',
              )}>
              <item.icon className={cn('h-5 w-5', isActive ? 'text-purple-700' : 'text-gray-500')} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Manage Availability Button */}
      {user?.isStarAccount && (
        <div className="px-4 pb-2">
          <button
            onClick={() => navigate('/manage-availability')}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transition-all">
            <Clock className="h-5 w-5" />
            Manage Availability
          </button>
        </div>
      )}

      {/* Become a Star Button */}
      {user && (
        <div className="p-4 pt-2">
          <button
            onClick={() => navigate('/become-star')}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transition-all">
            <Star className="h-5 w-5" />
            {user.isStarAccount ? 'Connect Accounts' : 'Become a Star'}
          </button>
        </div>
      )}

      {/* Logout Button */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 hover:text-red-600 transition-colors">
          <LogOut className="h-5 w-5" />
          <span>Log Out</span>
        </button>
      </div>
    </>
  );
}; 